<template>
  <TenderStep
    :current-step="currentStep"
    :total-steps="totalSteps"
    :error-transition="false"
  >
    <template slot="media">
      <slot name="media" />
    </template>

    <template slot="header">
      <div class="question-big">
        {{ $t("requestTender.name.question") }}
      </div>
    </template>

    <div class="relative">
      <JoszakiInput
        ref="nameInput"
        v-model="newTender.name.value"
        name="name"
        type="text"
        :placeholder="$t('requestTender.name.placeholder')"
        autofocus
        :show-error-msg="false"
        :validation-state="v$.name"
        @blur="v$.name.$touch()"
        @input="trackNameInputOnce"
        @keyup.enter.exact.native="emitNext('enter')"
      />
      <span
        v-if="!v$.name.$invalid && newTender.name.value"
        class="absolute right-3 top-1/2 -translate-y-1/2 text-positive pointer-events-none"
      >
        <IconComponent class="fa-lg" icon="check" />
      </span>
    </div>

    <template slot="error">
      <div v-if="v$.name.$error">
        {{ v$.name.$errors[0]?.$message }}
      </div>
    </template>

    <template slot="footer">
      <WizardNextButton :disabled="v$.name.$invalid" @click="emitNext()" />
    </template>
  </TenderStep>
</template>

<script>
import { computed, toRefs, useContext } from "@nuxtjs/composition-api";
import { useVuelidate } from "@vuelidate/core";
import WizardNextButton from "./WizardNextButton.vue";
import TenderStep from "@/components/TenderStep";
import { useNewTender } from "~/composables/tender/useNewTender";
import {
  useTrackWizardClick,
  WIZARD_ELEMENT_IDS,
} from "~/composables/tracking/useTrackWizardClick";

export default {
  name: "NameStepModule",
  components: { TenderStep, WizardNextButton },
  props: {
    currentStep: { type: Number, required: true },
    totalSteps: { type: Number, required: true },
    moduleSlug: { type: String, default: "name" },
  },
  emits: ["next"],
  setup(props) {
    const {
      $validation: { rules },
    } = useContext();
    const newTender = useNewTender();
    const { currentStep, moduleSlug } = toRefs(props);
    const { trackWizardClick, trackNextAttempt } = useTrackWizardClick({
      currentStep,
      moduleSlug,
    });

    // Local rules rather than the global useNewTender ones, so a hidden step
    // (conductor config) never contributes an invalid `name` to a shared v$.
    const localRules = computed(() => ({
      name: {
        required: rules.required,
        minLength: rules.minLength(3),
        maxLength: rules.maxLength(25),
      },
    }));
    const v$ = useVuelidate(localRules, { name: newTender.name });

    return { newTender, v$, trackWizardClick, trackNextAttempt };
  },
  data() {
    return { nameInputTracked: false };
  },
  methods: {
    trackNameInputOnce() {
      if (this.nameInputTracked) return;
      this.nameInputTracked = true;
      this.trackWizardClick(WIZARD_ELEMENT_IDS.NAME_INPUT);
    },
    emitNext(trigger = "click") {
      this.trackNextAttempt({ trigger, disabled: this.v$.name.$invalid });
      if (this.v$.name.$invalid) {
        this.v$.name.$touch();
        return;
      }
      this.newTender.saveToStorage();
      this.$emit("next", { field: "name" });
    },
  },
};
</script>
